/**
 * Pure rules for sizing one row of a quantity composition on Cold War (MDOT-25670): what a typed
 * quantity, market value or weight asks the position-sizing endpoint, and what its answer does to the
 * grid. Every number a sized row ends up with is Cold War's; the frontend only rebalances cash, which
 * stays the AUM remainder (`../derive`).
 *
 * | state              | row                                            | values                 | save    |
 * |--------------------|------------------------------------------------|------------------------|---------|
 * | `sizing`           | editable, the edited cell shows what was typed | untouched, dimmed      | waits   |
 * | answered `OK`      | editable                                       | Cold War's, price too  | ok      |
 * | held by value      | held by value: weight and market value only    | Cold War's, or cleared | ok      |
 * | answered other     | unpriced (`../market-date/pricing`)            | cleared                | blocked |
 * | `failed`           | editable, the edited cell says why             | untouched              | ok      |
 * | `failed` valuation | editable, value and weight flagged as stale    | the previous day's     | blocked |
 *
 * The values stay untouched while a row is sized, so a call that fails has nothing to revert, and the
 * cash row never moves on a number Cold War did not give. A row stays editable while it is sized: a
 * value typed over one still waiting starts a new sizing, and `attempt` tells one sizing from the
 * next, so the one superseded is never asked and an answer that lands after the row was reset,
 * repriced or sized again is dropped. Nothing here calls the API or touches the builder.
 *
 * A valuation is a sizing nobody typed: a reprice asks Cold War for the value of the shares a row
 * already holds at the new day, and the row shows its previous day's value until the answer lands.
 * When none does, that value is flagged as stale rather than cleared, since the price at the day is
 * still right, and the save waits until a retry, a typed edit or another day values the row.
 */
import type { InstrumentPricing, PositionSizingRequest } from "@eu.mdotm.api/sphere";
import { labDiv, labMul } from "$root/utils/monetary-math";
import type { Instrument } from "../../const";
import type { QuantityEditResult } from "../derive";
import { rebalanceCash } from "../derive";
import { applyPricing, isUnpriced, pricingIdentifier } from "../market-date/pricing";
import { isHeldBlocked } from "../held-issues/heldIssue";
import { isLocked } from "../new-instruments/checks";
import type { SizingAnswer } from "./answer";
import { rememberedValue, withRememberedValue } from "./valueByDate";

/** The cell the user typed in, and the column it belongs to. */
export type SizingField = "QUANTITY" | "MARKET_VALUE" | "WEIGHT";

/** What the user typed: `value` as the cell reads it, so a weight is a percentage. */
export type SizingEdit = { field: SizingField; value: number };

/** `valuationDate` marks a valuation (see above): the day the row's value was asked at. */
export type PositionSizing =
	| { status: "sizing"; edit: SizingEdit; attempt: number; requestedAt: string; valuationDate?: string }
	| {
			status: "failed";
			edit: SizingEdit;
			attempt: number;
			requestedAt: string;
			valuationDate?: string;
			failedAt: string;
			message: string;
	  };

/**
 * What sizing a row on Cold War adds to it. One named block for the same reason as the other row
 * states in `../../const`: dropping the feature drops this type, and the compiler names every reader.
 */
export type InstrumentPositionSizingAttributes = {
	positionSizing?: PositionSizing;
};

/** Whether the row waits on Cold War: its sized cells are dimmed, and the save waits with it. */
export function isSizing(row: Instrument): boolean {
	return row.positionSizing?.status === "sizing";
}

/** Whether the row's last sizing got no answer. */
export function isSizingFailed(row: Instrument): boolean {
	return row.positionSizing?.status === "failed";
}

/** Whether the row's valuation got no answer, so its value and weight are still the previous day's. */
export function isValuationFailed(row: Instrument): boolean {
	return row.positionSizing?.status === "failed" && row.positionSizing.valuationDate != null;
}

/** How many rows show a stale value: each keeps the save from going through. */
export function unvaluedCount(rows: Iterable<Instrument>): number {
	let count = 0;
	for (const row of rows) {
		if (isValuationFailed(row)) {
			count += 1;
		}
	}
	return count;
}

/** True while any row waits on Cold War. */
export function hasPendingSizing(rows: Iterable<Instrument>): boolean {
	for (const row of rows) {
		if (isSizing(row)) {
			return true;
		}
	}
	return false;
}

/**
 * Whether a value typed in `field` on the row can be sent to Cold War. Cash is the AUM remainder and
 * never sized; an unpriced row, a row its check has locked and a held row its issue blocks take no edit
 * at all; a row held by value has no share count to type; and a share row still waiting for its price
 * has nothing to size against yet. A row already being sized can be sized again: the new edit
 * supersedes the one waiting.
 */
export function canSize(row: Instrument, field: SizingField): boolean {
	if (row.isCash || isUnpriced(row) || isLocked(row) || isHeldBlocked(row)) {
		return false;
	}
	if (row.heldByValue && field === "QUANTITY") {
		return false;
	}
	if (!row.heldByValue && row.price == null) {
		return false;
	}
	return pricingIdentifier(row) != null;
}

/**
 * A typed value as it can be sent: a finite amount ≥ 0, which is all the endpoint accepts. Anything
 * else is no edit at all. No rounding: whole shares are Cold War's to decide.
 */
export function sanitiseEdit(edit: SizingEdit): SizingEdit | undefined {
	if (!Number.isFinite(edit.value)) {
		return undefined;
	}
	return { ...edit, value: Math.max(0, edit.value) };
}

/** The row as it waits on Cold War for `edit`; `valuationDate` when the edit is a valuation at that day. */
export function startSizing(
	row: Instrument,
	params: { edit: SizingEdit; attempt: number; now: string; valuationDate?: string },
): Instrument {
	const { edit, attempt, now, valuationDate } = params;
	return {
		...row,
		positionSizing: { status: "sizing", edit, attempt, requestedAt: now, ...(valuationDate ? { valuationDate } : {}) },
	};
}

/** Thrown when a sizing is asked of a portfolio with no currency: Cold War sizes every value in one. */
export class MissingSizingCurrencyError extends Error {
	constructor() {
		super("The portfolio has no currency to size the position in.");
		this.name = "MissingSizingCurrencyError";
	}
}

/**
 * Thrown when Cold War answers `OK` without a market value or a weight, or for a share row without the shares or the price it came
 * from, or answers for a row already held by value without its market value.
 */
export class IncompleteSizingAnswerError extends Error {
	constructor(ticker: string | undefined) {
		super(`Cold War sized ${ticker ?? "the position"} without its market value, weight, shares or price.`);
		this.name = "IncompleteSizingAnswerError";
	}
}

/**
 * What one sizing asks: the edited row, sized by exactly the one value typed. A weight goes out as a
 * fraction of the AUM, which is how the endpoint reads it. Throws {@link MissingSizingCurrencyError}
 * when the portfolio has no currency.
 */
export function sizingRequest(
	row: Instrument,
	edit: SizingEdit,
	context: { aum: number; currency: string | undefined; date: string },
): PositionSizingRequest {
	if (!context.currency) {
		throw new MissingSizingCurrencyError();
	}
	const request: PositionSizingRequest = {
		ticker: pricingIdentifier(row),
		currency: context.currency,
		amount: context.aum,
		referenceDate: context.date,
	};
	if (edit.field === "QUANTITY") {
		return { ...request, shares: edit.value };
	}
	if (edit.field === "MARKET_VALUE") {
		return { ...request, value: edit.value };
	}
	return { ...request, weight: labDiv(edit.value, 100) };
}

/**
 * The share rows that still wait on their value at `date`: each priced in shares there, holding a share
 * count, and not given a value at the day for that count and `aum` yet. Each is sized by its own
 * shares, so Cold War values it as it quotes it. A row already waiting on a typed sizing is left to
 * that answer; one whose valuation failed is asked again.
 */
export function valuationsAt(
	rows: Iterable<Instrument>,
	date: string,
	aum: number,
): Array<{ id: string; edit: SizingEdit }> {
	const valuations: Array<{ id: string; edit: SizingEdit }> = [];
	for (const row of rows) {
		if (
			row.quantity != null &&
			!isSizing(row) &&
			canSize(row, "QUANTITY") &&
			rememberedValue(row, date, aum) === undefined
		) {
			valuations.push({ id: row.id, edit: { field: "QUANTITY", value: row.quantity } });
		}
	}
	return valuations;
}

/**
 * The rows a reprice leaves valued without a call, cleared of a failed valuation: the value they show
 * is the one Cold War gave at the day, so it is no longer stale. Only the rows that change are returned.
 */
export function settledValuations(rows: Iterable<Instrument>, date: string, aum: number): Instrument[] {
	const settled: Instrument[] = [];
	for (const row of rows) {
		if (isValuationFailed(row) && rememberedValue(row, date, aum) !== undefined) {
			settled.push({ ...row, positionSizing: undefined });
		}
	}
	return settled;
}

/**
 * Whether Cold War's answer holds `row` by value, not in shares. `NOT_IN_MARKET_DATA` does only for a
 * row held by value already: Cold War has no market data for a nested portfolio or a live proxy, yet
 * sizes it by value all the same. A share row it answers so for has no price, and stays blocked.
 */
export function isHeldByValueAnswer(answer: SizingAnswer, row: Instrument): boolean {
	if (answer.status === "NOT_SHAREABLE") {
		return true;
	}
	return answer.status === "NOT_IN_MARKET_DATA" && row.heldByValue === true;
}

/** Whether `attempt` is still the sizing the row waits on — the question to ask before sending it, and again once it is answered. */
export function isCurrentSizing(row: Instrument | undefined, attempt: number): row is Instrument {
	return row?.positionSizing?.status === "sizing" && row.positionSizing.attempt === attempt;
}

/**
 * Writes Cold War's answer about row `id` into the composition, and rebalances cash (I1).
 *
 * `OK` sizes the row with Cold War's numbers and the price it used, so the price on show is always
 * the one the numbers came from, and a share row remembers its value and weight at `date` for the
 * shares it now holds and `aum` (`./valueByDate`). An answer held by value ({@link isHeldByValueAnswer}) means Cold War holds the
 * instrument by value, not in shares, and the row turns held by value exactly as a price call answering `WEIGHT` turns it: its
 * share count and price go, and only its weight or market value can be typed from then on. When Cold
 * War sized it anyway — a weight or a market value asked, answered with its value and weight — the row
 * takes those; a share row whose quantity was typed has its market value and weight cleared too, and
 * cash absorbs what it held.
 * Any other status means Cold War could not size the row at the day, and the row goes unpriced
 * exactly as a price call that found no price leaves it. An answer that is
 * no longer about the sizing the row waits on changes nothing. Throws
 * {@link IncompleteSizingAnswerError} when an answer with a market value lacks its weight, an `OK`
 * lacks its market value or a share row's shares or price, or an answer held by value for a row held by
 * value already lacks its market value: the row fails with its values untouched
 * rather than show numbers that do not add up.
 */
export function applySizingAnswer(params: {
	rows: Instrument[];
	id: string;
	attempt: number;
	answer: SizingAnswer;
	aum: number;
	date: string;
}): QuantityEditResult | undefined {
	const { rows, id, attempt, answer, aum, date } = params;
	const target = rows.find((row) => row.id === id);
	if (!isCurrentSizing(target, attempt)) {
		return undefined;
	}

	if (answer.status !== "OK") {
		if (target.heldByValue && isHeldByValueAnswer(answer, target) && answer.value == null) {
			throw new IncompleteSizingAnswerError(answer.ticker);
		}
		const identifier = pricingIdentifier(target);
		const cleared = rows.map((row) => (row.id === id ? { ...row, positionSizing: undefined } : row));
		if (!identifier) {
			return rebalanceCash(cleared, aum);
		}
		const pricing: InstrumentPricing = isHeldByValueAnswer(answer, target)
			? { identifier, holdingMode: "WEIGHT" }
			: { identifier, holdingMode: "UNPRICED" };
		const settled = applyPricing({ rows: cleared, pricing: new Map([[identifier, pricing]]), aum, date });
		if (!isHeldByValueAnswer(answer, target)) {
			return settled;
		}
		const sized =
			answer.value == null ? { marketValue: undefined, weight: undefined } : sizedValue(answer, answer.value);
		return rebalanceCash(
			settled.rows.map((row) => (row.id === id ? { ...row, ...sized } : row)),
			aum,
		);
	}

	if (answer.value == null || (!target.heldByValue && (answer.shares == null || answer.price == null))) {
		throw new IncompleteSizingAnswerError(answer.ticker);
	}

	const sized = sizedValue(answer, answer.value);
	return rebalanceCash(
		rows.map((row) => {
			if (row.id !== id) {
				return row;
			}
			if (row.heldByValue) {
				return { ...row, positionSizing: undefined, unpriced: false, ...sized };
			}
			return {
				...row,
				positionSizing: undefined,
				unpriced: false,
				quantity: answer.shares,
				price: answer.price,
				...sized,
				valueByDate:
					answer.shares == null
						? row.valueByDate
						: withRememberedValue(row, date, { shares: answer.shares, aum, ...sized }),
			};
		}),
		aum,
	);
}

/**
 * Cold War's market value, and its weight read as a percentage. Throws
 * {@link IncompleteSizingAnswerError} when it sends no weight: the weight is Cold War's, never derived here.
 */
function sizedValue(answer: SizingAnswer, marketValue: number): { marketValue: number; weight: number } {
	if (answer.weight == null) {
		throw new IncompleteSizingAnswerError(answer.ticker);
	}
	return { marketValue, weight: labMul(answer.weight, 100) };
}

/**
 * The row once its sizing got no answer: its values are what they were before the edit, and the
 * edit, when it failed and why stay on it for the edited cell to show. A valuation that failed keeps
 * its `valuationDate`, which is what flags the row's value as stale. `undefined` when the row has
 * moved on from that sizing.
 */
export function failSizing(
	row: Instrument | undefined,
	params: { attempt: number; now: string; message: string },
): Instrument | undefined {
	if (!isCurrentSizing(row, params.attempt) || row.positionSizing?.status !== "sizing") {
		return undefined;
	}
	return {
		...row,
		positionSizing: { ...row.positionSizing, status: "failed", failedAt: params.now, message: params.message },
	};
}
